import { memo, useMemo, useState } from 'react';
import flatMap from 'lodash/flatMap';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import { Filter } from '@/components/FilterManager';
import EntityDefaultChildren from '@/components/FilterManager/EntityFilter/components/EntityDefaultChildren';
import { getDefaultOperators } from '@/components/FilterManager/EntityFilterManager/utils';
import { IntegrationProviderEnum } from '@/types/schema';

import { taskGroupByFilterStyles } from './styles';
import { TaskGroupByFilterProps } from './types';
import { getTaskGroupByStaticOptions, getTaskTagGroupByOptions } from './utils';

const overrides = { classes: { Popover: { paper: taskGroupByFilterStyles } } };

const filterOptions = (options: FilterOption[], search: string) => {
  return options.filter((option) =>
    option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
};

const FILTER_GROUP_CONFIG: Record<string, { key: string; labelKey: string; order: number }> = {
  [IntegrationProviderEnum.Jira]: {
    key: IntegrationProviderEnum.Jira,
    labelKey: 'Jira:JiraFields',
    order: 0,
  },
  default: {
    key: 'tags',
    labelKey: 'Common:Form.Tags',
    order: 1,
  },
};

const TaskGroupByFilter = (props: TaskGroupByFilterProps) => {
  const { t } = useTranslation(['Jira']);
  const operators = getDefaultOperators(t);

  const [search, setSearch] = useState('');
  const [open, { off, toggle }] = useToggle(false);
  const {
    defaultPlaceholder,
    filterId,
    taskViewConfigs,
    value: valueProp,
    onChange: onChangeProp,
    isMyTasks,
  } = props;

  const groups = useMemo<FilterGroup[]>(() => {
    const filterGroups: FilterGroup[] = [
      { id: 'standard', options: getTaskGroupByStaticOptions(t, taskViewConfigs, isMyTasks) },
    ];

    const groupedTagsOptions = Object.entries(getTaskTagGroupByOptions(taskViewConfigs)).sort(
      ([a], [b]) => {
        const aConfig = FILTER_GROUP_CONFIG[a] || FILTER_GROUP_CONFIG.default;
        const bConfig = FILTER_GROUP_CONFIG[b] || FILTER_GROUP_CONFIG.default;

        return aConfig.order - bConfig.order;
      },
    );

    for (const [groupKey, options] of groupedTagsOptions) {
      const config = FILTER_GROUP_CONFIG[groupKey] || FILTER_GROUP_CONFIG.default;
      filterGroups.push({ id: config.key, label: t(config.labelKey), options });
    }

    return filterGroups;
  }, [isMyTasks, t, taskViewConfigs]);

  const value = useMemo(() => {
    const options = flatMap(groups.map((group) => group.options));
    const option = options.find(({ value }) => value === valueProp);

    return option ? [option] : [];
  }, [groups, valueProp]);

  const onValueChange = useEvent((nextValue: FilterOption[]) => {
    off();
    onChangeProp(nextValue[0].value);
  });

  const filteredGroups = useMemo(
    () =>
      groups.reduce<FilterGroup[]>((accumulator, group) => {
        const filteredOptions = filterOptions(group.options, search);

        if (filteredOptions.length) {
          accumulator.push({ ...group, options: filteredOptions });
        }

        return accumulator;
      }, []),
    [groups, search],
  );

  return (
    <Filter
      isSingleSelect
      asyncOptions={{ search: { value: search, onChange: setSearch } }}
      defaultPlaceholder={defaultPlaceholder}
      groups={filteredGroups}
      open={open}
      overrides={overrides}
      showActionButtons={false}
      value={value}
      onChange={onValueChange}
      onToggle={toggle}>
      <EntityDefaultChildren
        defaultPlaceholder={defaultPlaceholder}
        id={filterId}
        selectedOperator={operators[0]}
        selectedValues={value.map(({ label }) => label)}
      />
    </Filter>
  );
};

export default memo(TaskGroupByFilter);
